/**
 * The 1920×1080 canvas. Every slot is drawn from the shared geometry module exactly as
 * OBS will draw it: the item box (dashed) and the visible video (test card, cropped
 * and scaled like the nested CAM-n scene). Plain DOM and pointer events.
 */
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type Dispatch, type PointerEvent as ReactPointerEvent } from 'react';
import {
  CAMERA_SOURCE_SIZE,
  nameplatePlacement,
  REFERENCE_CANVAS,
  slotGeometry,
  stackingOrder,
  type ItemGeometry,
  type LayoutFile,
  type LayoutSlot,
  type Rect,
} from '@ioda/shared';
import type { EditorAction } from '../model/editor.ts';
import { slotBox } from '../model/editor.ts';
import { HANDLES, resizeRect, snapMove, snapResize, type Guide, type Handle } from '../model/snap.ts';
import { ShortcutList } from './Shortcuts.tsx';

interface Props {
  layout: LayoutFile;
  selectedSlotId: string | null;
  dispatch: Dispatch<EditorAction>;
}

interface Gesture {
  slotId: string;
  handle: Handle | null;
  pointerId: number;
  startX: number;
  startY: number;
  startRect: Rect;
  others: Rect[];
}

/** CSS for a rect given in item-local coordinates, rotated about the item position. */
function localRectStyle(rect: Rect, g: ItemGeometry, k: number): CSSProperties {
  return {
    left: (g.position.x + rect.x) * k,
    top: (g.position.y + rect.y) * k,
    width: rect.width * k,
    height: rect.height * k,
    transformOrigin: `${-rect.x * k}px ${-rect.y * k}px`,
    transform: g.rotation === 0 ? undefined : `rotate(${g.rotation}deg)`,
  };
}

function SlotView({ slot, selected, k, onPointerDown }: { slot: LayoutSlot; selected: boolean; k: number; onPointerDown: (e: ReactPointerEvent, handle: Handle | null) => void }) {
  const g = slotGeometry(slot);
  const plate = nameplatePlacement(slot, g);
  const resizable = selected && slot.rotation === 0 && slot.boundsType !== 'OBS_BOUNDS_NONE';
  const cardStyle: CSSProperties = {
    ...localRectStyle(g.content, g, k),
    backgroundSize: `${CAMERA_SOURCE_SIZE.width * g.scale.x * k}px ${CAMERA_SOURCE_SIZE.height * g.scale.y * k}px`,
    backgroundPosition: `${-g.sourceCrop.left * g.scale.x * k}px ${-g.sourceCrop.top * g.scale.y * k}px`,
  };
  return (
    <div className={`slot${selected ? ' selected' : ''}${slot.hidden ? ' parked' : ''}`} data-slot={slot.slotId}>
      <div className="slot-content" style={cardStyle} onPointerDown={(e) => onPointerDown(e, null)}>
        <span className="slot-label">
          {slot.slotId}
          {slot.hidden ? ' (parked)' : ''}
        </span>
      </div>
      <div className="slot-box" style={localRectStyle(g.box, g, k)} onPointerDown={(e) => onPointerDown(e, null)}>
        {resizable &&
          HANDLES.map((h) => (
            <span
              key={h}
              className={`handle handle-${h}`}
              onPointerDown={(e) => {
                e.stopPropagation();
                onPointerDown(e, h);
              }}
            />
          ))}
      </div>
      {plate && (
        <div
          className="nameplate-marker"
          title={`Nameplate hint: ${slot.nameplate?.anchor ?? ''}`}
          style={{
            left: plate.point.x * k,
            top: plate.point.y * k,
            fontSize: Math.max(9, 22 * k),
            transform: `translate(${plate.horizontal === 'center' ? '-50%' : '0'}, ${plate.vertical === 'bottom' ? '-100%' : '0'})`,
          }}
        >
          nameplate
        </div>
      )}
    </div>
  );
}

export function Stage({ layout, selectedSlotId, dispatch }: Props) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [k, setK] = useState(0.5);
  const [guides, setGuides] = useState<Guide[]>([]);
  const gesture = useRef<Gesture | null>(null);

  // Scale the canvas to fit its container.
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const fit = () => {
      const { width, height } = el.getBoundingClientRect();
      setK(Math.max(0.1, Math.min((width - 32) / REFERENCE_CANVAS.width, (height - 32) / REFERENCE_CANVAS.height)));
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Release a gesture if the window loses the pointer (alt-tab mid-drag).
  useEffect(() => {
    const cancel = () => {
      gesture.current = null;
      setGuides([]);
    };
    window.addEventListener('blur', cancel);
    return () => window.removeEventListener('blur', cancel);
  }, []);

  const begin = (slot: LayoutSlot, e: ReactPointerEvent, handle: Handle | null) => {
    if (e.button !== 0) return;
    e.preventDefault();
    dispatch({ type: 'select', slotId: slot.slotId });
    if (slot.rotation !== 0) return; // rotated slots are edited from the inspector
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    dispatch({ type: 'beginGesture' });
    gesture.current = {
      slotId: slot.slotId,
      handle,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      startRect: slotBox(slot),
      others: layout.slots.filter((s) => s.slotId !== slot.slotId && !s.hidden).map((s) => slotGeometry(s).contentBounds),
    };
  };

  const onPointerMove = (e: ReactPointerEvent) => {
    const gst = gesture.current;
    if (!gst || e.pointerId !== gst.pointerId) return;
    const dx = (e.clientX - gst.startX) / k;
    const dy = (e.clientY - gst.startY) / k;
    const noSnap = e.altKey;
    let next: { rect: Rect; guides: Guide[] };
    if (gst.handle === null) {
      const moved = { ...gst.startRect, x: gst.startRect.x + dx, y: gst.startRect.y + dy };
      next = noSnap ? { rect: moved, guides: [] } : snapMove(moved, gst.others);
    } else {
      const resized = resizeRect(gst.startRect, gst.handle, dx, dy, e.shiftKey);
      next = noSnap ? { rect: resized, guides: [] } : snapResize(resized, gst.handle, gst.others, e.shiftKey);
    }
    setGuides(next.guides);
    dispatch({ type: 'setSlotBox', slotId: gst.slotId, rect: next.rect, record: false });
  };

  const end = (e: ReactPointerEvent) => {
    if (gesture.current && e.pointerId === gesture.current.pointerId) {
      gesture.current = null;
      setGuides([]);
    }
  };

  const bySlotId = new Map(layout.slots.map((s) => [s.slotId, s]));
  // Draw bottom → top so DOM order matches OBS stacking; the selection stays on top.
  const ordered = stackingOrder(layout)
    .map((id) => bySlotId.get(id))
    .filter((s): s is LayoutSlot => s !== undefined);

  return (
    <div className="stage-wrap" ref={wrapRef}>
      <div
        className="stage"
        style={{ width: REFERENCE_CANVAS.width * k, height: REFERENCE_CANVAS.height * k }}
        onPointerMove={onPointerMove}
        onPointerUp={end}
        onPointerCancel={end}
        onPointerDown={(e) => {
          if (e.target === e.currentTarget) dispatch({ type: 'select', slotId: null });
        }}
      >
        <div className="stage-center-x" style={{ left: (REFERENCE_CANVAS.width / 2) * k }} />
        <div className="stage-center-y" style={{ top: (REFERENCE_CANVAS.height / 2) * k }} />
        {ordered.map((slot) => (
          <SlotView key={slot.slotId} slot={slot} selected={slot.slotId === selectedSlotId} k={k} onPointerDown={(e, h) => begin(slot, e, h)} />
        ))}
        {guides.map((g, i) => (
          <div key={i} className={`guide guide-${g.axis}`} style={g.axis === 'x' ? { left: g.at * k } : { top: g.at * k }} />
        ))}
      </div>
      <ShortcutList />
    </div>
  );
}
